'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import { useId, useState, type ComponentProps, type ReactNode } from 'react'

/**
 * A plus that becomes a minus: one bar stays, the other folds away and turns as it goes. Cheaper to
 * read than swapping two icons, and it carries the open/close motion instead of cutting to it.
 */
function ToggleMark({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="relative mt-[0.55rem] flex size-3.5 shrink-0 items-center justify-center transition-colors duration-200"
    >
      <span className="absolute h-px w-full rounded-full bg-current" />
      <span
        className={clsx(
          'absolute h-full w-px rounded-full bg-current transition-transform duration-300 ease-out motion-reduce:transition-none',
          open ? 'scale-y-0 rotate-90' : 'scale-y-100 rotate-0',
        )}
      />
    </span>
  )
}

/**
 * One question in a FAQ list: a full-width toggle and an answer that opens by growing into its own
 * height rather than appearing at it.
 *
 * The animation is the `0fr → 1fr` grid-row trick: the answer stays in the document at its natural
 * height — so it is in the HTML for search engines, and finds and copies like ordinary text —
 * while the row holding it is what animates. `height` cannot be transitioned from `auto`, and
 * measuring it in JS would have to be redone at every breakpoint the text reflows at.
 */
export function Faq({
  id,
  question,
  answer,
  ...props
}: { id: string; question: ReactNode; answer: ReactNode } & Omit<ComponentProps<'div'>, 'id'>) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const questionId = useId()

  return (
    <div id={id} {...props}>
      <button
        type="button"
        id={questionId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className={clsx(
          'flex w-full cursor-pointer items-start justify-between gap-6 py-4 text-left text-base/7 transition-colors duration-200',
          open ? 'text-accent-deep dark:text-accent-bright' : 'text-mist-950 hover:text-accent-deep dark:text-white',
        )}
      >
        <span>{question}</span>
        <ToggleMark open={open} />
      </button>

      <div
        id={panelId}
        role="region"
        // The region is named by the question it answers. An unnamed region is a landmark a screen
        // reader announces as "region" and nothing else, which is worse than no landmark at all when
        // a page carries eight of them.
        aria-labelledby={questionId}
        // A closed answer is still in the document — that is deliberate, so the text is in the HTML
        // for search engines and finds and copies like ordinary prose. But it was also still in the
        // tab order and still read out: a keyboard user tabbing through the FAQ landed on links
        // inside answers that were folded to nothing, with no way to see where they were. `inert`
        // takes it out of focus and out of the accessibility tree without taking it out of the page.
        inert={!open}
        className={clsx(
          'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        {/* The answer rises into place as the row grows, so opening reads as one movement rather
            than a box getting taller with text already in it. */}
        <div className="overflow-hidden">
          <div
            className={clsx(
              'flex flex-col gap-2 pr-6 pb-5 text-sm/7 text-mist-700 transition-all duration-300 ease-out motion-reduce:transition-none sm:pr-12 dark:text-mist-400',
              open ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
            )}
          >
            {answer}
          </div>
        </div>
      </div>
    </div>
  )
}
